import React from 'react';
import type { ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';

import type { SmartsFeedbackComponent_contentContributors$key } from '@confluence/quick-summary/entry-points/SmartsFeedbackContributors.ts';

import { useNudgeContributorFilter } from './useNudgeContributorFilter.ts';
import type { NudgeContributorFilter_contentContributors$key } from './__generated__/NudgeContributorFilter_contentContributors.graphql.ts';

export type PageRovoContributorsRef = NudgeContributorFilter_contentContributors$key &
	SmartsFeedbackComponent_contentContributors$key;

type Props = {
	contentId: string;
	skip: boolean;
	contributorsFragmentRef?: NudgeContributorFilter_contentContributors$key;
	children: (isCurrentUserContributor: boolean) => ReactNode;
};

const NudgeContributorFilterFromQuery = ({ contentId, skip, children }: Props) => {
	const { isCurrentUserContributor } = useNudgeContributorFilter(contentId, skip);
	return <>{children(isCurrentUserContributor)}</>;
};

const NudgeContributorFilterFromFragment = ({
	contributorsFragmentRef,
	children,
	...props
}: Props & { contributorsFragmentRef: NudgeContributorFilter_contentContributors$key }) => {
	const data = useFragment(
		graphql`
			fragment NudgeContributorFilter_contentContributors on ContentContributors {
				isCurrentUserContributor
			}
		`,
		contributorsFragmentRef,
	);
	// An unavailable status must use the existing loading/error path.
	if (data.isCurrentUserContributor == null) {
		return <NudgeContributorFilterFromQuery {...props}>{children}</NudgeContributorFilterFromQuery>;
	}
	return <>{children(data.isCurrentUserContributor)}</>;
};

export const NudgeContributorFilter = (props: Props): React.JSX.Element => {
	if (props.contentId && !props.skip && props.contributorsFragmentRef) {
		return (
			<NudgeContributorFilterFromFragment
				{...props}
				contributorsFragmentRef={props.contributorsFragmentRef}
			/>
		);
	}
	return <NudgeContributorFilterFromQuery {...props} />;
};
